<template>
  <!-- Estilo unificado con SelectLayersModal y UpdateStatusesModal:
       rounded-lg + border + shadow, HEADER Meta con título + close button (SVG)
       y FOOTER con v-button. Se abre desde LayoutBuilder al clickear una
       polilínea con ancho > 0 en viewerMode='progress'. -->
  <b-modal :active.sync="isModalActive" :width="960" scroll="keep" :can-cancel="['escape','outside']">
    <div class="flex flex-col max-h-[85vh] rounded-lg bg-white overflow-hidden border border-[#E4E6EB] shadow-lg">

      <!-- HEADER (fijo) -->
      <header class="flex-none flex justify-between items-center px-5 py-3.5 border-b border-[#E4E6EB] bg-white">
        <div class="flex-1 min-w-0">
          <h2 class="text-[17px] font-semibold text-gray-900 break-words whitespace-normal">
            {{ $t('linear_advance') }}
          </h2>
        </div>
        <button
          @click="isModalActive = false"
          class="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-[#f2f2f7] hover:text-[#3a6bc8] transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-[#c9d6ef] focus:ring-offset-1"
          aria-label="Cerrar">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
          </svg>
        </button>
      </header>

      <!-- BODY (scrollable; header/footer fijos) -->
      <section class="flex-1 overflow-y-auto px-4 py-4 bg-white">
        <!-- FILA 1: longitud total (ancho completo). -->
        <div class="bg-gray-50 border border-[#E4E6EB] rounded px-3 py-2 text-sm mb-4">
          <span class="text-gray-500 uppercase text-xs font-semibold">{{ $t('total_length') }}:</span>
          <strong class="ml-2 text-gray-900">{{ totalLength.toFixed(2) }} m</strong>
        </div>

        <!-- FILA 2: dos columnas. -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">

          <!-- ───────── COLUMNA 1: inicio de avance · metros ───────── -->
          <div class="space-y-4">
            <!-- Selector visual de dirección — mini-SVG con la forma real de la
                 polilínea, dos extremos clickeables y chevrons indicando el sentido
                 del avance. Si directionLocked=true (ya hay avance previo) los
                 extremos no responden a click y solo sirven de referencia visual. -->
            <div v-if="projectedPoints.length >= 2">
              <label class="flex items-center gap-1 text-xs uppercase text-gray-500 font-semibold mb-2">
                {{ $t('advance_start') }}
                <b-icon v-if="directionLocked" pack="fas" icon="lock" size="is-small" />
              </label>
              <div class="flex justify-center bg-gray-50 border border-gray-200 rounded-md p-1">
                <svg :viewBox="`0 0 ${svgWidth} ${svgHeight}`" preserveAspectRatio="xMidYMid meet" class="block w-full h-auto">
                  <!-- Polilínea real escalada al viewport -->
                  <polyline
                    :points="pointsString"
                    fill="none"
                    stroke="#9ca3af"
                    stroke-width="2.5"
                    stroke-linejoin="round"
                    stroke-linecap="round"
                  />

                  <!-- Chevrons distribuidos a lo largo del path, orientados según
                       la dirección elegida. Comunican el sentido del avance aunque
                       la polilínea sea zigzagueante. -->
                  <polygon
                    v-for="(arrow, i) in arrowsForDirection"
                    :key="'arr-' + i"
                    :points="_GetArrowPoints(arrow.cx, arrow.cy, arrow.angle)"
                    class="fill-[#5577BB] pointer-events-none"
                  />

                  <!-- Extremo A (vertex[0]) -->
                  <g
                    @click="_OnEndpointClick('l2r')"
                    :class="['endpoint-grp', { 'endpoint-grp--locked': directionLocked }]"
                  >
                    <circle
                      :cx="projectedPoints[0].x"
                      :cy="projectedPoints[0].y"
                      r="11"
                      :class="['endpoint', { 'endpoint--selected': direction === 'l2r' }]"
                    />
                    <text
                      :x="projectedPoints[0].x"
                      :y="projectedPoints[0].y + 4"
                      text-anchor="middle"
                      class="fill-white text-[11px] font-bold select-none pointer-events-none">A</text>
                  </g>

                  <!-- Extremo B (vertex[last]) -->
                  <g
                    @click="_OnEndpointClick('r2l')"
                    :class="['endpoint-grp', { 'endpoint-grp--locked': directionLocked }]"
                  >
                    <circle
                      :cx="projectedPoints[projectedPoints.length - 1].x"
                      :cy="projectedPoints[projectedPoints.length - 1].y"
                      r="11"
                      :class="['endpoint', { 'endpoint--selected': direction === 'r2l' }]"
                    />
                    <text
                      :x="projectedPoints[projectedPoints.length - 1].x"
                      :y="projectedPoints[projectedPoints.length - 1].y + 4"
                      text-anchor="middle"
                      class="fill-white text-[11px] font-bold select-none pointer-events-none">B</text>
                  </g>
                </svg>
              </div>
              <p class="text-xs text-gray-500 mt-1">
                {{ directionLocked ? $t('advance_start_locked_hint', [_StartLetter]) : $t('advance_start_hint', [_StartLetter]) }}
              </p>
            </div>

            <!-- Metros ejecutados: título + marcador visual sobre el dibujo (SVG2)
                 y DEBAJO el input numérico. Arrastrar/clicar el dibujo fija los
                 metros desde el inicio elegido; está sincronizado con el input. -->
            <div>
              <label class="block text-xs uppercase text-gray-500 font-semibold mb-2">{{ $t('executed_meters') }}</label>

              <!-- SVG2 — marcador de metros: replica el dibujo (con los extremos
                   A/B como referencia), pinta lo ejecutado desde el inicio y el
                   handle se arrastra a lo largo del trazo. -->
              <div v-if="projectedPoints.length >= 2"
                class="flex justify-center bg-gray-50 border border-gray-200 rounded-md p-1 cursor-crosshair touch-none"
                @pointerdown="_OnMetersPointerDown"
                @pointermove="_OnMetersPointerMove"
                @pointerup="_OnMetersPointerUp"
                @pointerleave="_OnMetersPointerUp">
                <svg ref="metersSvg" :viewBox="`0 0 ${svgWidth} ${svgHeight}`"
                  preserveAspectRatio="xMidYMid meet" class="block w-full h-auto">
                  <!-- Trazo base -->
                  <polyline :points="pointsString" fill="none" stroke="#d1d5db"
                    stroke-width="3" stroke-linejoin="round" stroke-linecap="round" />
                  <!-- Porción ejecutada (desde el inicio hasta el handle) -->
                  <polyline v-if="meters > 0" :points="executedPointsString" fill="none"
                    :stroke="metersColor" stroke-width="4" stroke-linejoin="round" stroke-linecap="round" />

                  <!-- Flechas de sentido (paralelas al trazo) según el inicio
                       elegido — guía discreta de en qué dirección va el avance. -->
                  <polygon
                    v-for="(arrow, i) in arrowsForDirection"
                    :key="'marr-' + i"
                    :points="_GetArrowPoints(arrow.cx, arrow.cy, arrow.angle)"
                    class="fill-gray-400 pointer-events-none"
                  />

                  <!-- Extremos A/B como referencia (el de inicio resaltado según
                       la dirección elegida; aquí NO son clickeables). -->
                  <circle :cx="projectedPoints[0].x" :cy="projectedPoints[0].y" r="11"
                    :class="['endpoint', { 'endpoint--selected': direction === 'l2r' }]" />
                  <text :x="projectedPoints[0].x" :y="projectedPoints[0].y + 4" text-anchor="middle"
                    class="fill-white text-[11px] font-bold select-none pointer-events-none">A</text>
                  <circle :cx="projectedPoints[projectedPoints.length - 1].x" :cy="projectedPoints[projectedPoints.length - 1].y" r="11"
                    :class="['endpoint', { 'endpoint--selected': direction === 'r2l' }]" />
                  <text :x="projectedPoints[projectedPoints.length - 1].x" :y="projectedPoints[projectedPoints.length - 1].y + 4" text-anchor="middle"
                    class="fill-white text-[11px] font-bold select-none pointer-events-none">B</text>

                  <!-- Handle arrastrable en la posición de los metros -->
                  <circle :cx="metersHandle.x" :cy="metersHandle.y" r="8"
                    :fill="metersColor" stroke="#fff" stroke-width="2" class="cursor-grab" />
                </svg>
              </div>

              <!-- Input numérico DEBAJO del dibujo. Validación manual (dígitos +
                   1 punto + 2 decimales). -->
              <b-input
                v-model="metersInput"
                inputmode="decimal"
                placeholder="0.00"
                expanded
                class="mt-2"
                @input="_OnMetersInput"
                @blur="_OnMetersBlur"
              />

              <p v-if="selectedStatus" class="text-xs text-gray-500 mt-1">
                {{ $t('available_for_status', [selectedStatus.label, remainingLength.toFixed(2)]) }}
              </p>
            </div>
          </div>

          <!-- ───────── COLUMNA 2: estado · fecha · progreso ───────── -->
          <div class="space-y-4">
            <!-- ESTADO — primero defino "qué" hago, luego "cuánto". Label propio
                 (Tailwind) para que sea idéntico a los demás, sin b-field. -->
            <div>
              <label class="block text-xs uppercase text-gray-500 font-semibold mb-2">{{ $t('status') }}</label>
              <multiselect
                v-model="selectedStatus"
                :options="statusesForSelect"
                track-by="value"
                label="label"
                :placeholder="$t('select_status')"
                :show-labels="false"
                :allow-empty="true"
                :max-height="200"
                open-direction="bottom"
                class="advance-status-select"
              >
                <template slot="option" slot-scope="{ option }">
                  <div class="flex items-center gap-2">
                    <span class="status-swatch" :style="{ backgroundColor: option.color }"></span>
                    <span>{{ option.label }}</span>
                  </div>
                </template>
                <template slot="singleLabel" slot-scope="{ option }">
                  <div class="flex items-center gap-2">
                    <span class="status-swatch" :style="{ backgroundColor: option.color }"></span>
                    <span>{{ option.label }}</span>
                  </div>
                </template>
                <template slot="noOptions">{{ $t('no_options_available') }}</template>
              </multiselect>

              <!-- Aviso del modo consecutivo (solo strict/capped). -->
              <p v-if="orderMode !== 'free'" class="text-xs text-gray-500 mt-1 flex items-center gap-1">
                <b-icon pack="fas" icon="circle-info" size="is-small" />
                {{ orderMode === 'strict' ? $t('consecutive_hint_strict') : $t('consecutive_hint_capped') }}
              </p>
            </div>

            <!-- Fecha del registro (equiv. al `start_date` legacy). Default hoy. -->
            <div>
              <label class="block text-xs uppercase text-gray-500 font-semibold mb-2">{{ $t('registration_date') }}</label>
              <!-- append-to-body: el calendario se renderiza en <body> para que
                   NO lo recorte el overflow del cuerpo/modal (antes se ocultaba). -->
              <b-datepicker
                v-model="startDate"
                :max-date="todayDate"
                position="is-top-right"
                icon="calendar-day"
                icon-pack="fas"
                append-to-body
                editable
              />
            </div>

            <div class="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 flex flex-col gap-1.5 text-[13px]"
              v-if="totalLength > 0 && (hasRejects || statusBreakdown.length > 0)">
              <div class="flex justify-between items-center">
                <span class="text-[11px] text-gray-500 uppercase font-semibold tracking-wide">{{ (hasRejects ? $t('current_progress') : $t('progress_by_status')) }}:</span>
                <span class="text-xs text-gray-500">/ {{ totalLength.toFixed(2) }} m</span>
              </div>

              <!-- Modelo de bolsas (capa con rechazos): flujo parcial. Mostramos la
                   distribución vigente del segmento: por-iniciar (gris), cada etapa con
                   sus metros (su color) y rechazado (color de rechazo, re-ejecutable). -->
              <div v-if="hasRejects" class="flex flex-col gap-1.5 mt-1">
                <template v-if="poolBreakdown.length">
                  <div v-for="brk in poolBreakdown" :key="'pool-' + brk.key" class="flex items-center gap-2 text-xs px-1 py-0.5 rounded">
                    <span class="status-swatch" :style="{ backgroundColor: brk.color }"></span>
                    <span class="truncate min-w-[90px] max-w-[130px] text-gray-700 font-medium" :title="brk.label">{{ brk.label || '—' }}</span>
                    <div class="relative flex-1 h-1.5 bg-[#eef0f4] rounded-full overflow-hidden">
                      <div class="absolute left-0 top-0 h-full transition-[width] duration-200"
                           :style="{ width: _PoolPercent(brk.meters) + '%', backgroundColor: brk.color }"></div>
                    </div>
                    <span class="tabular-nums font-semibold text-gray-800 min-w-[70px] text-right">{{ brk.meters.toFixed(2) }} m</span>
                  </div>
                </template>
                <div v-else class="text-xs text-gray-500 py-1">{{ $t('no_advance_registered') }}</div>
              </div>

              <!-- Clásico (zanjas): cada estado es un proceso paralelo (cavar, tapar,
                   fibra, etc.) que arranca desde 0 y avanza independiente del resto. Lo
                   mostramos como una mini-barra por estado, donde el ancho refleja el %
                   cubierto sobre el total de la polilínea. El estado que el usuario está
                   editando se refuerza con el preview del nuevo delta (rayas diagonales). -->
              <div v-else class="flex flex-col gap-1.5 mt-1">
                <div v-for="brk in statusBreakdown" :key="'brk-' + brk.value"
                     :class="['flex items-center gap-2 text-xs px-1 py-0.5 rounded transition-colors', _IsEditingStatus(brk.value) ? 'bg-[#eef3fb]' : '']">
                  <span class="status-swatch" :style="{ backgroundColor: brk.color }"></span>
                  <span class="truncate min-w-[90px] max-w-[130px] text-gray-700 font-medium" :title="brk.label">{{ brk.label || '—' }}</span>
                  <div class="relative flex-1 h-1.5 bg-[#eef0f4] rounded-full overflow-hidden">
                    <!-- Parte ya registrada (existente, sólida). -->
                    <div class="absolute left-0 top-0 h-full transition-[width] duration-200"
                         :style="{
                           width: _ExistingPercentOfStatus(brk.value) + '%',
                           backgroundColor: brk.color,
                         }"></div>
                    <!-- Preview del nuevo delta sobre el status editado
                         (rayas diagonales para distinguirlo de lo ya registrado). -->
                    <div v-if="_IsEditingStatus(brk.value) && meters > 0"
                         class="status-breakdown__bar-preview"
                         :style="{
                           left: _ExistingPercentOfStatus(brk.value) + '%',
                           width: _PreviewPercentOfStatus(brk.value) + '%',
                           backgroundColor: brk.color,
                         }"></div>
                  </div>
                  <span class="tabular-nums font-semibold text-gray-800 min-w-[70px] text-right">{{ brk.meters.toFixed(2) }} m</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      <!-- Banner de error inline cuando el backend rechaza el apply. -->
      <div v-if="errorMessage" class="flex-none bg-red-50 border-t border-red-200 px-4 py-2 text-red-700 text-sm">
        {{ errorMessage }}
      </div>

      <!-- FOOTER (fijo) -->
      <footer class="flex-none px-5 py-3.5 border-t border-[#E4E6EB] bg-white">
        <div class="w-full flex items-center justify-between gap-2">
          <v-button color="grey" icon="door-closed" size="small"
                    :disabled="isApplying"
                    @click="isModalActive = false">
            {{ $t('cancel') }}
          </v-button>
          <v-button icon="check" size="small"
                    :loading="isApplying"
                    :disabled="isApplying"
                    @click="_OnApply">
            {{ $t('apply') }}
          </v-button>
        </div>
      </footer>
    </div>
  </b-modal>
</template>

<script>
import Multiselect from 'vue-multiselect'
import { simulatePools } from '../../helpers/advance-pool.helper'
import {
  computeStatusEligibility,
  currentFlowState as computeCurrentFlowState,
  existingMetersOfStatus,
  netMetersOfStatus,
} from '../../helpers/advance-eligibility.helper'

const SVG_WIDTH  = 440
const SVG_HEIGHT = 130
const SVG_PAD    = 18    // padding interno del viewport para que los circles
                         // de los extremos no salgan del SVG
const ARROW_COUNT = 3    // cantidad de chevrons distribuidos a lo largo del path
const ARROW_SIZE  = 7    // tamaño base del chevron (radio en SVG units)

export default {
  name: 'RegisterAdvanceModal',

  components: { Multiselect },

  props: {},

  data() {
    return {
      isModalActive: false,
      // Flag que se enciende mientras el padre persiste el avance en el
      // backend. Mientras dure, el botón Aplicar muestra loading y el modal
      // no se cierra. El padre llama `hide()` si éxito o `setError(msg)`
      // si falla.
      isApplying: false,
      errorMessage: '',
      // Fecha del avance (Date object). Default hoy. Se manda al backend
      // como YYYY-MM-DD en _OnApply.
      startDate: new Date(),
      // El padre pasa los valores iniciales vía show(opts). La copia mutable
      // vive acá adentro — cancelar descarta sin afectar el padre. Solo se
      // propaga al hacer Apply (vía epc-apply).
      totalLength: 0,
      direction:   'l2r',
      // `meters` ahora significa "metros nuevos en este registro" (delta),
      // no el total acumulado. Cada Apply crea un nuevo segment con este
      // valor y el status elegido — el avance previo no se modifica.
      meters:      0,
      // Espejo de texto de `meters` para el campo (validación numérica manual).
      metersInput: '0',
      selectedStatus: null,
      // Lista de status disponibles para la capa actual.
      // Formato uniforme: [{ value, label, color }]. El padre la calcula
      // en _GetStatusesForLayer (statuses reales de la capa o hardcoded).
      statuses:    [],
      // Segmentos ya registrados previamente (no se editan desde acá).
      // Cada uno: { meters, status, statusLabel, statusColor }.
      previousSegments: [],
      // Vertices crudos de la polilínea ([{x,y}, ...]) — el padre los pasa
      // para que dibujemos el mini-SVG y los extremos clickeables.
      vertices:    [],
      // Si true, la dirección ya está fijada por un avance previo y los
      // endpoints quedan como referencia visual (no responden a click).
      directionLocked: false,

      // Modo de orden de estados (free | strict | capped). Define qué estados
      // son elegibles y el tope del input. 'free' = comportamiento histórico.
      orderMode: 'free',

      svgWidth:  SVG_WIDTH,
      svgHeight: SVG_HEIGHT,

      // True mientras se arrastra el handle del marcador de metros (SVG2).
      metersDragging: false,
    }
  },

  computed: {
    /**
     * Vertices proyectados al viewport SVG (escala uniforme + Y flippeado).
     * Centrados en el viewport sin estirar (preserva la forma real).
     */
    projectedPoints() {
      const verts = this.vertices
      if (!verts || verts.length < 2) return []

      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity
      for (const v of verts) {
        if (v.x < minX) minX = v.x
        if (v.x > maxX) maxX = v.x
        if (v.y < minY) minY = v.y
        if (v.y > maxY) maxY = v.y
      }

      // Span mínimo para evitar divisiones por cero en polilíneas planas
      // (todas las x iguales o todas las y iguales).
      const w = Math.max(maxX - minX, 1e-9)
      const h = Math.max(maxY - minY, 1e-9)

      const innerW = SVG_WIDTH  - 2 * SVG_PAD
      const innerH = SVG_HEIGHT - 2 * SVG_PAD
      const scale  = Math.min(innerW / w, innerH / h)

      // Centramos en el viewport: si la polilínea es muy alargada, sobra
      // espacio en el eje corto.
      const drawW = w * scale
      const drawH = h * scale
      const offX  = SVG_PAD + (innerW - drawW) / 2
      const offY  = SVG_PAD + (innerH - drawH) / 2

      // Y se flippea porque SVG va Y↓ y el DXF va Y↑.
      return verts.map(v => ({
        x: offX + (v.x - minX) * scale,
        y: offY + (maxY - v.y) * scale,
      }))
    },

    pointsString() {
      return this.projectedPoints.map(p => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ')
    },

    /**
     * Lista de chevrons a renderizar — ubicados en posiciones interpoladas
     * (25%, 50%, 75% del path), orientados según la dirección elegida.
     *
     * Si direction='r2l' invertimos la secuencia de vertices, así los
     * chevrons apuntan del extremo B hacia el A (sentido visual del avance).
     */
    arrowsForDirection() {
      const pts = this.projectedPoints
      if (pts.length < 2) return []

      const sequence = this.direction === 'l2r' ? pts : pts.slice().reverse()

      // Acumulado de longitudes para muestrear a posiciones interpoladas.
      const cumLen = [0]
      for (let i = 1; i < sequence.length; i++) {
        const dx = sequence[i].x - sequence[i - 1].x
        const dy = sequence[i].y - sequence[i - 1].y
        cumLen.push(cumLen[i - 1] + Math.hypot(dx, dy))
      }
      const totalLen = cumLen[cumLen.length - 1]
      if (totalLen < 1e-6) return []

      const arrows = []
      for (let n = 1; n <= ARROW_COUNT; n++) {
        const targetLen = totalLen * (n / (ARROW_COUNT + 1))

        // Buscar el segmento que contiene targetLen.
        let segIdx = 1
        while (segIdx < cumLen.length && cumLen[segIdx] < targetLen) segIdx++
        if (segIdx >= cumLen.length) break

        const segStart = cumLen[segIdx - 1]
        const segEnd   = cumLen[segIdx]
        const segSpan  = segEnd - segStart
        const t = segSpan > 1e-9 ? (targetLen - segStart) / segSpan : 0

        const a = sequence[segIdx - 1]
        const b = sequence[segIdx]
        const cx = a.x + (b.x - a.x) * t
        const cy = a.y + (b.y - a.y) * t
        // Ángulo en SVG coords (Y↓), no en mundo (Y↑). Como ya usamos los
        // proyectados, el ángulo sale natural para el render.
        const angle = Math.atan2(b.y - a.y, b.x - a.x)

        arrows.push({ cx, cy, angle })
      }
      return arrows
    },

    /** Letra del extremo de inicio según direction — para mostrar en el hint. */
    _StartLetter() {
      return this.direction === 'l2r' ? 'A' : 'B'
    },

    // ── Marcador de metros (SVG2): el trazo recorrido en el sentido elegido ──

    /** Puntos proyectados en el orden de avance (invertidos si r2l). */
    metersSeq() {
      const pts = this.projectedPoints
      if (pts.length < 2) return []
      return this.direction === 'l2r' ? pts : pts.slice().reverse()
    },

    /** Longitudes acumuladas (en unidades SVG) a lo largo de metersSeq. */
    metersCum() {
      const seq = this.metersSeq
      const cum = [0]
      for (let i = 1; i < seq.length; i++) {
        cum.push(cum[i - 1] + Math.hypot(seq[i].x - seq[i - 1].x, seq[i].y - seq[i - 1].y))
      }
      return { cum, total: cum[cum.length - 1] || 0 }
    },

    /** Punto de inicio (extremo elegido). */
    metersStartPoint() {
      return this.metersSeq[0] || { x: 0, y: 0 }
    },

    /** Color del marcador: el del estado elegido o el azul de marca. */
    metersColor() {
      return (this.selectedStatus && this.selectedStatus.color) || '#3a6bc8'
    },

    /** Posición del handle según los metros actuales (fracción del total). */
    metersHandle() {
      const { total } = this.metersCum
      if (total <= 0 || this.totalLength <= 0) return { ...this.metersStartPoint, i: 0 }
      const frac = Math.max(0, Math.min(this.meters / this.totalLength, 1))
      return this._PointAtArc(frac * total)
    },

    /** Puntos de la polilínea ejecutada (desde el inicio hasta el handle). */
    executedPointsString() {
      const seq = this.metersSeq
      if (seq.length < 2) return ''
      const h = this.metersHandle
      const hi = (h.i != null) ? h.i : seq.length - 1
      const pts = []
      for (let k = 0; k < hi; k++) pts.push(seq[k])
      pts.push({ x: h.x, y: h.y })
      return pts.map(p => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ')
    },

    /**
     * Metros ya registrados PARA EL STATUS SELECCIONADO. Es lo que define
     * cuánto le falta a ese proceso para llegar al final de la polilínea.
     * Cada estado avanza independiente (cavar, tapar, fibra... cada uno
     * empieza desde 0 y llega hasta totalLength).
     */
    existingMetersOfSelectedStatus() {
      if (!this.selectedStatus) return 0
      return this._ExistingMetersOfStatus(this.selectedStatus.value)
    },

    /**
     * Elegibilidad por estado (Map value → { eligible, cap }).
     *
     * DOS comportamientos según la capa:
     *  - CON rechazos (flujo aceptar/rechazar, ej. eléctrico): MÁQUINA DE ESTADOS.
     *    La polilínea está en UN estado a la vez = último positivo alcanzado
     *    procesando los segmentos en orden CRONOLÓGICO. Un rechazo (100%) RESETEA
     *    al inicio ("sin ejecutar" → re-ejecutar). Elegibles = el SIGUIENTE positivo
     *    (aceptación) + su(s) rechazo(s) emparejado(s). Tras el último positivo:
     *    proceso terminado (nada). Cada acción registra el total (sin parciales).
     *  - SIN rechazos (progresión normal): modelo clásico free/strict/capped.
     */
    statusEligibility() {
      // Fuente única compartida con el gesto inline (advance-eligibility.helper).
      return computeStatusEligibility({
        statuses:         this.statuses,
        previousSegments: this.previousSegments,
        totalLength:      this.totalLength,
        orderMode:        this.orderMode,
      })
    },

    /**
     * Opciones del multiselect con `$isDisabled` según elegibilidad — los
     * estados no elegibles se muestran deshabilitados (vue-multiselect los
     * respeta y no permite seleccionarlos).
     */
    statusesForSelect() {
      return this.statuses.map(s => {
        const e = this.statusEligibility.get(s.value)
        return { ...s, $isDisabled: !(e && e.eligible) }
      })
    },

    /**
     * Máximo permitido en el input "Metros ejecutados": el tope del estado
     * seleccionado según el modo (ver statusEligibility). Sin estado, el total.
     */
    remainingLength() {
      if (!this.selectedStatus) return this.totalLength
      const e = this.statusEligibility.get(this.selectedStatus.value)
      if (e) return e.cap
      return Math.max(0, this.totalLength - this.existingMetersOfSelectedStatus)
    },

    /** True si la capa tiene rechazos (weight<0) → aplica el modelo de bolsas. */
    hasRejects() {
      return (this.statuses || []).some(s => Number(s.weight) < 0)
    },

    /**
     * Estado de las BOLSAS del segmento (solo capas con rechazos), simulando el
     * historial cronológico como transferencias entre etapas. Ver _SimulatePools.
     */
    pools() {
      return this._SimulatePools()
    },

    /**
     * Desglose para mostrar en el modal: por-iniciar (gris), cada etapa positiva con
     * metros vigentes (su color), y rechazado (color de rechazo, re-ejecutable).
     */
    poolBreakdown() {
      const p = this.pools
      const eps = 1e-6
      const out = []
      if ((p.unstarted || 0) > eps) {
        out.push({ key: 'unstarted', label: this.$t('pending_execution'), color: '#9ca3af', meters: p.unstarted })
      }
      for (let k = 0; k < p.M; k++) {
        if ((p.bags[k] || 0) > eps) {
          const st = this.statuses[p.positives[k]]
          out.push({ key: 'bag' + k, label: st.label, color: st.color, meters: p.bags[k] })
        }
      }
      // Rechazados POR ESTADO (cada estado de rechazo con su propio color/label).
      if (p.rejectedByStatus) {
        for (const [sv, meters] of p.rejectedByStatus) {
          if ((meters || 0) <= eps) continue
          const rej = (this.statuses || []).find(s => s.value === sv)
          out.push({
            key: 'rej-' + sv,
            label: rej ? rej.label : this.$t('rejected'),
            color: (rej && rej.color) || '#e17055',
            meters,
          })
        }
      }
      return out
    },

    /**
     * Estado NETO actual de la máquina de estados (solo capas con rechazos): el
     * positivo alcanzado procesando previousSegments en orden CRONOLÓGICO con la
     * MISMA regla de retroceso que statusEligibility (REJECT_RETURN_MODE). El
     * segmento está en UN estado a la vez, no acumula. Devuelve
     * { value, label, color, meters:totalLength } o null si currentPosLevel = -1
     * (sin avance / reset al inicio → 0). Mantener la lógica de currentPosLevel EN
     * SYNC con statusEligibility.
     */
    currentFlowState() {
      // Fuente única compartida con el gesto inline (advance-eligibility.helper).
      return computeCurrentFlowState({
        statuses:         this.statuses,
        previousSegments: this.previousSegments,
      })
    },

    /**
     * Acumulado por status — {value, label, color, meters}.
     * Se renderiza como lista debajo de la barra para que el usuario vea
     * cuánto lleva ejecutado por cada estado (ej. "🟡 En curso: 30m, 🟢 Completado: 5m").
     */
    todayDate() {
      return new Date()
    },

    statusBreakdown() {
      const map = new Map()
      for (const seg of this.previousSegments) {
        const key = seg.status
        if (!map.has(key)) {
          map.set(key, {
            value: seg.status,
            label: seg.statusLabel || '',
            color: seg.statusColor || '#9ca3af',
            meters: 0,
          })
        }
        map.get(key).meters += seg.meters || 0
      }
      // Si hay un nuevo delta + status seleccionado, lo sumamos al breakdown
      // (preview del próximo apply).
      if (this.meters > 0 && this.selectedStatus) {
        const key = this.selectedStatus.value
        if (!map.has(key)) {
          map.set(key, {
            value: this.selectedStatus.value,
            label: this.selectedStatus.label,
            color: this.selectedStatus.color,
            meters: 0,
          })
        }
        map.get(key).meters += this.meters
      }
      return [...map.values()]
    },
  },

  watch: {
    /**
     * Cuando cambia el status seleccionado, el "remainingLength" cambia
     * (cada status tiene su propio espacio disponible). Si el valor actual
     * de `meters` excede el nuevo máximo, lo clampeamos al borde.
     */
    selectedStatus() {
      if (this.meters > this.remainingLength) {
        this._SetMeters(this.remainingLength)
      }
    },
  },

  methods: {
    /**
     * Sanea el texto del campo de metros: solo dígitos + un punto + máx 2
     * decimales, y clampa el número a [0, remainingLength]. Mantiene `meters`
     * como Number (fuente de verdad para el resto de la lógica).
     */
    _OnMetersInput(val) {
      let s = String(val == null ? '' : val).replace(',', '.').replace(/[^\d.]/g, '')
      const dot = s.indexOf('.')
      if (dot !== -1) {
        const intPart = s.slice(0, dot)
        const decPart = s.slice(dot + 1).replace(/\./g, '').slice(0, 2)
        s = intPart + '.' + decPart
      }

      let n = parseFloat(s)
      if (!Number.isFinite(n) || n < 0) n = 0
      if (n > this.remainingLength) {
        n = Math.round(this.remainingLength * 100) / 100
        s = this._FmtMeters(n)   // reflejamos el clamp en el texto
      }

      this.metersInput = s
      this.meters = (s === '' || s === '.') ? 0 : n
    },

    /** Al salir del campo, normaliza el texto al valor numérico final. */
    _OnMetersBlur() {
      this._SetMeters(this.meters || 0)
    },

    /** Set programático: fija `meters` (redondeado a 2 dec.) y su espejo de texto. */
    _SetMeters(n) {
      const v = Number.isFinite(n) ? Math.round(n * 100) / 100 : 0
      this.meters = v
      this.metersInput = this._FmtMeters(v)
    },

    /** Formato limpio sin ceros de más: "0", "30", "30.03". */
    _FmtMeters(n) {
      const v = Math.round((Number(n) || 0) * 100) / 100
      return String(v)
    },

    /**
     * Abre el modal con los valores iniciales.
     * @param {Object} opts
     * @param {Number}  opts.totalLength        — longitud total de la polilínea (m)
     * @param {Array}   opts.vertices           — [{x,y}, ...] de la polilínea original
     * @param {String}  opts.direction          — 'l2r' | 'r2l' (default 'l2r')
     * @param {Boolean} opts.directionLocked    — si true, no se puede cambiar la dirección
     * @param {Array}   opts.previousSegments   — segmentos ya registrados (no editables acá).
     *                                            cada uno: { meters, status, statusLabel, statusColor }
     * @param {Array}   opts.statuses           — [{value, label, color}] disponibles
     */
    show(opts = {}) {
      this.totalLength      = opts.totalLength || 0
      this.direction        = opts.direction   || 'l2r'
      this.directionLocked  = !!opts.directionLocked
      this.previousSegments = Array.isArray(opts.previousSegments)
        ? opts.previousSegments
        : []
      this.statuses         = Array.isArray(opts.statuses) ? opts.statuses : []
      this.orderMode        = opts.orderMode || 'free'
      // `meters` siempre arranca en 0: este registro es DELTA, no total.
      // El usuario tipea cuánto se avanzó hoy.
      this._SetMeters(0)
      // El status arranca vacío para que el placeholder "Seleccione un estado"
      // sea visible y obligue al cliente a elegir conscientemente.
      this.selectedStatus   = null
      // Cada apertura del modal arranca con la fecha de hoy.
      this.startDate        = new Date()
      this.errorMessage     = ''
      this.isApplying       = false
      // Clonado superficial — solo necesitamos x,y para el SVG. Lo congelamos
      // en una copia para evitar que mutaciones externas alteren el render.
      this.vertices = (opts.vertices || []).map(v => ({ x: v.x, y: v.y }))
      this.isModalActive = true
    },

    hide() {
      this.isModalActive = false
      this.isApplying = false
      this.errorMessage = ''
    },

    /**
     * Click en uno de los extremos del SVG. Si la dirección está lockada
     * (ya hay avance previo), el click es no-op.
     */
    _OnEndpointClick(dir) {
      if (this.directionLocked) return
      this.direction = dir
    },

    // ── Interacción del marcador de metros (SVG2) ──

    /** Punto a lo largo de metersSeq a la distancia de arco `arc` (unidades SVG). */
    _PointAtArc(arc) {
      const seq = this.metersSeq
      const { cum } = this.metersCum
      if (seq.length < 2) return { ...(seq[0] || { x: 0, y: 0 }), i: 0 }
      for (let i = 1; i < seq.length; i++) {
        if (cum[i] >= arc) {
          const segLen = cum[i] - cum[i - 1] || 1e-9
          const t = Math.max(0, Math.min((arc - cum[i - 1]) / segLen, 1))
          const a = seq[i - 1], b = seq[i]
          return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, i }
        }
      }
      const last = seq[seq.length - 1]
      return { x: last.x, y: last.y, i: seq.length - 1 }
    },

    /** Proyecta (sx,sy) en coords SVG sobre el trazo → fija los metros (clamp). */
    _ProjectToMeters(sx, sy) {
      const seq = this.metersSeq
      const { cum, total } = this.metersCum
      if (seq.length < 2 || total <= 0) return
      let best = { d: Infinity, arc: 0 }
      for (let i = 1; i < seq.length; i++) {
        const a = seq[i - 1], b = seq[i]
        const abx = b.x - a.x, aby = b.y - a.y
        const len2 = abx * abx + aby * aby || 1e-9
        let t = ((sx - a.x) * abx + (sy - a.y) * aby) / len2
        t = Math.max(0, Math.min(t, 1))
        const px = a.x + abx * t, py = a.y + aby * t
        const d = (sx - px) * (sx - px) + (sy - py) * (sy - py)
        if (d < best.d) best = { d, arc: cum[i - 1] + t * Math.hypot(abx, aby) }
      }
      let m = (best.arc / total) * this.totalLength
      m = Math.max(0, Math.min(m, this.remainingLength))
      this._SetMeters(m)
    },

    /** Convierte las coords de cliente del evento a coords SVG y proyecta. */
    _ApplyMetersFromPointer(evt) {
      const svg = this.$refs.metersSvg
      if (!svg) return
      const rect = svg.getBoundingClientRect()
      if (!rect.width || !rect.height) return
      const sx = (evt.clientX - rect.left) * (this.svgWidth / rect.width)
      const sy = (evt.clientY - rect.top) * (this.svgHeight / rect.height)
      this._ProjectToMeters(sx, sy)
    },

    _OnMetersPointerDown(evt) {
      this.metersDragging = true
      this._ApplyMetersFromPointer(evt)
    },

    _OnMetersPointerMove(evt) {
      if (this.metersDragging) this._ApplyMetersFromPointer(evt)
    },

    _OnMetersPointerUp() {
      this.metersDragging = false
    },

    /**
     * Construye los puntos de un triángulo (chevron) centrado en (cx, cy),
     * orientado según `angle` (radianes). El triángulo nace apuntando hacia
     * +X y luego se rota.
     */
    _GetArrowPoints(cx, cy, angle) {
      const cos = Math.cos(angle)
      const sin = Math.sin(angle)
      // Coords locales — triángulo apuntando hacia +X.
      const local = [
        { x:  ARROW_SIZE,        y:  0                  }, // punta
        { x: -ARROW_SIZE * 0.6,  y:  ARROW_SIZE * 0.55  }, // base inferior
        { x: -ARROW_SIZE * 0.6,  y: -ARROW_SIZE * 0.55  }, // base superior
      ]
      return local
        .map(p => {
          const rx = p.x * cos - p.y * sin + cx
          const ry = p.x * sin + p.y * cos + cy
          return `${rx.toFixed(2)},${ry.toFixed(2)}`
        })
        .join(' ')
    },

    /**
     * Helpers para renderizar las mini-barras por status.
     * El `statusBreakdown` ya incluye el delta nuevo (preview); estos
     * separan lo "ya registrado" del "preview" para mostrarlos como
     * dos zonas distintas dentro de la misma mini-barra.
     */
    _IsEditingStatus(statusValue) {
      return this.selectedStatus && this.selectedStatus.value === statusValue
    },

    /** Metros ya registrados (sin contar el delta nuevo) para este status. */
    _ExistingMetersOfStatus(statusValue) {
      return existingMetersOfStatus(this.previousSegments, statusValue)
    },

    /**
     * Metros NETOS acumulados de un status en capas CON rechazos: se procesan
     * los segmentos en orden cronológico y un RECHAZO (weight<0) rechaza TODO
     * lo ejecutado → resetea el acumulado a 0. A diferencia de
     * _ExistingMetersOfStatus (suma cruda de todo el histórico), esto refleja el
     * ejecutado VIGENTE, que es lo que define cuánto FALTA por registrar
     * (totalLength − neto). El avance positivo es parcial y acumulativo (10 + 20
     * = 30); tras un rechazo vuelve a 0.
     */
    _NetMetersOfStatus(statusValue) {
      return netMetersOfStatus(this.previousSegments, this.statuses, statusValue)
    },

    /**
     * Simula el historial (previousSegments) como transferencias entre BOLSAS y
     * devuelve el estado vigente del segmento. Modelo de flujo parcial con retorno:
     *   - Positivo nivel 0 (Ejecutar): toma de (rechazado primero, luego por-iniciar) → bag[0].
     *   - Positivo nivel k≥1 (Aceptar): toma de bag[k-1] → bag[k].
     *   - Rechazo (emparejado con el accept de nivel k): toma de bag[k-1] → rechazado
     *     (esos metros vuelven a estar disponibles para re-ejecutar; NO resetea el resto).
     * La suma (por-iniciar + Σbags + rechazado) es siempre = totalLength.
     * Devuelve { L, positives, posLevelOfIdx, prevIdx, M, bags, unstarted, rejected }.
     */
    _SimulatePools() {
      // Modelo de bolsas compartido (advance-pool.helper) — misma simulación que
      // usa el visor al pintar el avance. Antes era una copia local duplicada.
      return simulatePools(this.previousSegments, this.statuses, this.totalLength)
    },

    /** % de la barra que ocupan `meters` sobre el total (clamp 0..100). */
    _PoolPercent(meters) {
      if (this.totalLength <= 0) return 0
      return Math.max(0, Math.min((Number(meters) || 0) / this.totalLength * 100, 100))
    },

    /** % del total que cubre lo ya registrado del status. */
    _ExistingPercentOfStatus(statusValue) {
      if (this.totalLength <= 0) return 0
      return Math.max(0, Math.min(this._ExistingMetersOfStatus(statusValue) / this.totalLength * 100, 100))
    },

    /** % del total que cubre el delta nuevo del status editado (preview). */
    _PreviewPercentOfStatus(statusValue) {
      if (this.totalLength <= 0 || !this._IsEditingStatus(statusValue)) return 0
      const existing = this._ExistingMetersOfStatus(statusValue)
      const available = Math.max(0, this.totalLength - existing)
      const preview = Math.min(this.meters || 0, available)
      return Math.max(0, Math.min(preview / this.totalLength * 100, 100))
    },

    _OnApply() {
      if (!this.selectedStatus) {
        this.$buefy?.toast?.open?.({
          message:  this.$t('select_status'),
          type:     'is-warning',
          position: 'is-top-right',
        })
        return
      }
      // Estado ya completo (sin margen): mensaje claro en vez de "introduzca
      // avance". Cubre el caso de tipear un valor > lo permitido (se clampa a 0).
      if (this.remainingLength <= 1e-6) {
        this.$buefy?.toast?.open?.({
          message:  this.$t('status_already_complete'),
          type:     'is-warning',
          position: 'is-top-right',
        })
        return
      }
      if (!this.meters || this.meters <= 0) {
        this.$buefy?.toast?.open?.({
          message:  this.$t('enter_executed_meters'),
          type:     'is-warning',
          position: 'is-top-right',
        })
        return
      }
      // Encendemos loading y emitimos. El padre va a persistir en backend
      // y llamar a `hide()` (éxito) o `setError(msg)` (fallo).
      this.errorMessage = ''
      this.isApplying = true
      this.$emit('epc-apply', {
        direction:   this.direction,
        meters:      this.meters,
        status:      this.selectedStatus.value,
        statusLabel: this.selectedStatus.label,
        statusColor: this.selectedStatus.color,
        startDate:   this._FormatYMD(this.startDate),
      })
    },

    /** Date → 'YYYY-MM-DD' para el payload del backend. */
    _FormatYMD(date) {
      const d = date instanceof Date ? date : new Date(date)
      const yyyy = d.getFullYear()
      const mm   = String(d.getMonth() + 1).padStart(2, '0')
      const dd   = String(d.getDate()).padStart(2, '0')
      return `${yyyy}-${mm}-${dd}`
    },

    /**
     * Llamado por el padre cuando la persistencia falla. Quita el loading
     * y muestra el error inline para que el usuario pueda reintentar
     * sin volver a tipear todo.
     */
    setError(message) {
      this.isApplying = false
      this.errorMessage = message || ''
    },
  },
}
</script>

<style scoped>
.endpoint-grp {
  cursor: pointer;
}

.endpoint-grp--locked {
  cursor: not-allowed;
}

.endpoint {
  fill: #d1d5db;
  stroke: #fff;
  stroke-width: 2;
  transition: fill 0.15s ease, stroke 0.15s ease;
}

.endpoint-grp:not(.endpoint-grp--locked):hover .endpoint {
  fill: #9ca3af;
}

.endpoint--selected {
  fill: #5577BB;   /* primary de la app */
  stroke: #fff;
}

.endpoint-grp:not(.endpoint-grp--locked):hover .endpoint--selected {
  fill: #385DA5;   /* primary-dark al hover */
}

/* Cuando está lockado, suavizamos el extremo no-seleccionado para
   reforzar visualmente "solo lectura". */
.endpoint-grp--locked .endpoint:not(.endpoint--selected) {
  opacity: 0.5;
}

/* Preview del nuevo delta: se renderiza al final del fill existente con
   rayas diagonales para distinguirlo de lo ya registrado. */
.status-breakdown__bar-preview {
  position: absolute;
  top: 0;
  height: 100%;
  background-image: repeating-linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.55) 0,
    rgba(255, 255, 255, 0.55) 2px,
    transparent 2px,
    transparent 5px
  );
  background-blend-mode: overlay;
  transition: width 0.2s ease, left 0.2s ease;
}

/* Swatch de color del status — renderizado en los slots option/singleLabel
   del multiselect. */
.status-swatch {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}

/* Multiselect del estado — alineado al azul de marca (tags, foco y opciones). */
.advance-status-select :deep(.multiselect__tags) {
  min-height: 40px;
  padding: 7px 40px 0 10px;
  font-size: 14px;
  border: 1px solid #e9e9ef;
  border-radius: 8px;
}

.advance-status-select :deep(.multiselect--active .multiselect__tags) {
  border-color: #c9d6ef;
  box-shadow: 0 0 0 2px rgba(58, 107, 200, 0.12);
}

.advance-status-select :deep(.multiselect__placeholder) {
  color: #9ca3af;
  padding-top: 2px;
}

.advance-status-select :deep(.multiselect__content-wrapper) {
  border: 1px solid #e9e9ef;
  border-radius: 8px;
  box-shadow: 0 10px 28px rgba(17, 24, 39, 0.12);
}

.advance-status-select :deep(.multiselect__option) {
  font-size: 13px;
  min-height: 36px;
}

/* Opción resaltada / seleccionada → azul de marca (en vez del verde default). */
.advance-status-select :deep(.multiselect__option--highlight),
.advance-status-select :deep(.multiselect__option--highlight::after) {
  background: #eef3fb;
  color: #2f5aad;
}

.advance-status-select :deep(.multiselect__option--selected),
.advance-status-select :deep(.multiselect__option--selected::after) {
  background: #e8eef8;
  color: #2f5aad;
  font-weight: 600;
}

.advance-status-select :deep(.multiselect__option--selected.multiselect__option--highlight),
.advance-status-select :deep(.multiselect__option--selected.multiselect__option--highlight::after) {
  background: #dbe6fa;
  color: #2f5aad;
}

.advance-status-select :deep(.multiselect__option--disabled) {
  background: #f7f7f9 !important;
  color: #c7c7cf !important;
  cursor: not-allowed;
}
</style>

<!-- CSS de vue-multiselect — fuera del scope para que aplique al dropdown
     (que se renderiza como portal y no respeta los estilos scoped). -->
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
